import { MessagingSettings } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/service.pb';
import { SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { useQueries } from 'react-query';
import { bindHTTP } from '@frontend/schema';

type TruelarkLocationSettingResponse = {
  setting?: {
    truelarkLocationSetting?: {
      businessId?: string;
    };
  };
};

const SchemaMessagingSettingsV1Service = bindHTTP(MessagingSettings);

type UseTruelarkDependencyArgs = {
  locationIds?: string[];
  orgId?: string;
  enabled?: boolean;
  isTruelarkDependencyRequired?: boolean;
};

/**
 * For bridgeline settings, our features depend on having a TrueLark business ID.
 * This hook checks if the user has the necessary scope to access the AI feature settings, and then queries for the TrueLark business ID.
 * If no business ID is found, the features dependent on TrueLark will be disabled.
 */
export const useTruelarkDependency = ({
  locationIds,
  orgId,
  enabled = true,
  isTruelarkDependencyRequired = true,
}: UseTruelarkDependencyArgs) => {
  const scopedLocationIds = locationIds?.filter(Boolean) ?? [];
  const hasScope = scopedLocationIds.length > 0 && !!orgId;
  const shouldQuery = enabled && isTruelarkDependencyRequired && hasScope;

  const messagingSettingsQueries = useQueries(
    scopedLocationIds.map((locationId) => {
      const request = {
        settingType: SettingType_Enum.TRUELARK_LOCATION_SETTING,
        orgId: orgId ?? '',
        locationId,
      };

      return {
        queryKey: ['tl-location-setting', request],
        queryFn: () => SchemaMessagingSettingsV1Service.Get(request),
        enabled: shouldQuery,
        retry: 0,
        retryOnMount: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
      };
    })
  );

  const businessIds = messagingSettingsQueries.map(
    (query) =>
      (query.data as TruelarkLocationSettingResponse | undefined)?.setting?.truelarkLocationSetting?.businessId ?? ''
  );

  const meetsTruelarkDependency =
    !isTruelarkDependencyRequired || (shouldQuery && businessIds.length > 0 && businessIds.every(Boolean));

  return {
    meetsTruelarkDependency,
    isLoading: isTruelarkDependencyRequired && messagingSettingsQueries.some((query) => query.isLoading),
    isError: isTruelarkDependencyRequired && messagingSettingsQueries.some((query) => query.isError),
  };
};
